import {
  ArrayUtil,
  PropertyAtPath,
  PropertyPath,
} from '@suntex.core/animation';
import type { Entities_Player_ProgressSummary_Response } from '@suntex.fim/api-client';

import { ModuleTagging, NodeModel, StarValue, StrictNodeModel } from '../types';

import { resolveFilterPath } from './resolveFilterPath';

export const MAX_MODULE_PROGRESS = 2;

/**
 * Computes the star value for a single leaf node (e.g. an individual game module)
 * based on its progress data in the progress summary.
 *
 * Progress is computed as `rawValue / (dataTarget ?? 1)`, clamped to [0, MAX_MODULE_PROGRESS]:
 * - None  (0): progress < 1
 * - Silver (1): progress >= 1
 * - Gold  (2): progress >= 2
 */
export function computeModuleStarValue(
  progressSummary: Entities_Player_ProgressSummary_Response,
  node: Pick<NodeModel, 'moduleSlug' | 'dataPath' | 'dataTarget'>,
): StarValue {
  if (!node.moduleSlug && !node.dataPath) {
    return StarValue.None;
  }

  const path: PropertyPath<Entities_Player_ProgressSummary_Response> =
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    node.dataPath ?? ['games', node.moduleSlug!];

  const data = ArrayUtil.getProperty(progressSummary, path) as PropertyAtPath<
    Entities_Player_ProgressSummary_Response,
    typeof path
  >;

  if (typeof data !== 'number') {
    return StarValue.None;
  }

  const progress = Math.max(
    0,
    Math.min(data / (node.dataTarget ?? 1), MAX_MODULE_PROGRESS),
  );
  return Math.floor(progress);
}

/**
 * Computes the aggregated star value for a category/parent node by recursively
 * evaluating all of its visible descendants.
 *
 * Aggregation rules (same as hub's applyProgress):
 * - Gold:   ALL visible leaf descendants have Gold stars
 * - Silver: ALL visible leaf descendants have at least Silver stars
 * - None:   otherwise
 */
export function computeCategoryStarValue(
  progressSummary: Entities_Player_ProgressSummary_Response,
  categoryNode: StrictNodeModel,
  moduleTagging: readonly ModuleTagging[],
): StarValue {
  return computeNodeStarValue(progressSummary, categoryNode, moduleTagging);
}

function computeNodeStarValue(
  progressSummary: Entities_Player_ProgressSummary_Response,
  node: StrictNodeModel,
  moduleTagging: readonly ModuleTagging[],
): StarValue {
  const children = getEffectiveChildren(node, moduleTagging);

  if (children.length > 0) {
    let goldCount = 0;
    let silverCount = 0;
    const total = children.length;

    for (const child of children) {
      const childStar = computeNodeStarValue(
        progressSummary,
        child,
        moduleTagging,
      );
      if (childStar === StarValue.Gold) {
        goldCount++;
        silverCount++;
      } else if (childStar === StarValue.Silver) {
        silverCount++;
      }
    }

    if (goldCount === total) return StarValue.Gold;
    if (silverCount === total) return StarValue.Silver;
    return StarValue.None;
  }

  return computeModuleStarValue(progressSummary, node);
}

function getEffectiveChildren(
  node: StrictNodeModel,
  moduleTagging: readonly ModuleTagging[],
): readonly StrictNodeModel[] {
  const children =
    node.filterTagPath && node.filterRoot
      ? resolveFilterPath(node, moduleTagging)
      : node.children;
  return children.filter((c) => !c.hidden);
}
